import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useCompanyLogo } from '../hooks/useCompanyLogo';
import ddLogo from './ui/dd.png';

// Type definitions
type SizePreset = 'sm' | 'md' | 'lg' | 'xl';

interface AnimatedLogoProps {
  className?: string;
  size?: SizePreset;
  style?: React.CSSProperties;
  alt?: string;
  [key: string]: any; // For additional props
}

/**
 * Industry-standard 3D Flip Card Logo Animation
 * Uses proper CSS 3D transforms with backface-visibility for seamless transitions
 * Alternates between Company Logo and Dance Digital Logo every 10 seconds
 */
const AnimatedLogo: React.FC<AnimatedLogoProps> = ({ 
  className = '', 
  size = 'md',
  style = {},
  alt = 'Logo',
  ...props 
}) => {
  const { logoUrl: companyLogoUrl, isLoading } = useCompanyLogo();
  const [isFlipped, setIsFlipped] = useState<boolean>(false);

  // Size presets
  const sizeClasses: Record<SizePreset, string> = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-20 h-20'
  };

  const sizeClass = sizeClasses[size] || sizeClasses.md;

  // Effect to handle logo flip every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIsFlipped(prev => !prev);
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <div 
        className={`${sizeClass} bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse flex items-center justify-center ${className}`}
        style={style}
        {...props}
      >
        <div className="w-6 h-6 bg-gray-300 dark:bg-gray-600 rounded"></div>
      </div>
    );
  }

  return (
    <div className={`${sizeClass} ${className}`} style={style} {...props}>
      {/* 3D Flip Container */}
      <motion.div
        className="relative w-full h-full"
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{
          duration: 1,
          ease: "easeInOut"
        }}
        style={{
          transformStyle: "preserve-3d",
          perspective: "1000px"
        }}
      >
        {/* Front Face - Company Logo */}
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden"
          }}
        >
          <img 
            src={companyLogoUrl}
            alt={`${alt} - Company Logo`}
            className="w-full h-full object-contain"
            onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
              console.warn('Company logo failed to load, using fallback');
              const target = e.target as HTMLImageElement;
              if (target.src !== ddLogo) {
                target.src = ddLogo;
              }
            }}
          />
        </div>

        {/* Back Face - Dance Digital Logo */}
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)"
          }}
        >
          <img 
            src={ddLogo}
            alt={`${alt} - Dance Digital Logo`}
            className="w-full h-full object-contain"
            onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
              console.warn('Dance Digital logo failed to load');
            }}
          />
        </div>
      </motion.div>
    </div>
  );
};


export default AnimatedLogo;



